import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useMangaSource } from '../context/MangaSourceContext';
import { useAniList } from '../context/AniListContext';
import { useTitlePreference } from '../context/TitlePreferenceContext';
import { useAlert } from '../context/ToastContext';
import { formatChapterProgress, getTotalChapters, getTotalVolumes, calculateMangaProgress, getMangaMediaStatusBadge } from '../utils/timeFormatter';
import { formatReleaseDate } from '../services/groupingService';
import { getDisplayTitle } from '../hooks/useDisplayTitle';
import { updateMangaProgress, updateMangaVolumeProgress, updateAnimeStatus, updateMediaScore } from '../services/aniListService';
import { getSimpleCache } from '../services/cacheService';
import { LOCAL_STORAGE_KEYS } from '../config/constants';
import { CardGridSkeleton } from './common/SkeletonLoader';
import EpisodeCounter from './EpisodeCounter';
import StatusButton from './StatusButton';
import SiteLinksModal from './SiteLinksModal';
import ScoreInput from './ScoreInput';
import { useLinkSettings } from '../context/LinkSettingsContext';
import './MarathonMode.css';
import './Dashboard.css';

const MangaMarathonMode = () => {
    const { token } = useAniList();
    const { mangaGroups, mangaUserData, isLoading, refreshData, latestChapterMap } = useMangaSource();
    const { titlePreference } = useTitlePreference();
    const { showError } = useAlert();
    const { readLinks } = useLinkSettings();

    const [selectedGroup, setSelectedGroup] = useState(null);
    const [optimisticProgress, setOptimisticProgress] = useState(new Map());
    const [updatingProgress, setUpdatingProgress] = useState(new Set());
    const [optimisticUpdates, setOptimisticUpdates] = useState(new Map());
    const [changingStatus, setChangingStatus] = useState(new Set());
    const [optimisticVolumes, setOptimisticVolumes] = useState(new Map());
    const [updatingVolumes, setUpdatingVolumes] = useState(new Set());
    const [optimisticScores, setOptimisticScores] = useState(new Map());
    const [updatingScores, setUpdatingScores] = useState(new Set());
    const [showReadModal, setShowReadModal] = useState(false);
    const [selectedMangaForModal, setSelectedMangaForModal] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [loadedPages, setLoadedPages] = useState(new Set([1]));

    const GROUPS_PER_PAGE = 100;

    // Get all groups sorted by remaining chapters (most to least), EXCLUDING dropped manga
    const marathonGroups = useMemo(() => {
        if (!mangaGroups || mangaGroups.length === 0) return [];

        const groupsWithRemaining = mangaGroups.map(group => {
            const entries = group.anime || group.manga || [];
            const nonDropped = entries.filter(entry => entry.status !== 'DROPPED');

            if (nonDropped.length === 0) {
                return { ...group, remainingChapters: 0, hasNonDropped: false };
            }

            let totalChapters = 0;
            let readChapters = 0;
            let hasUnknownTotal = false;

            nonDropped.forEach(entry => {
                const total = getTotalChapters(entry, latestChapterMap);
                const read = entry.progress || 0;
                if (total !== null) {
                    totalChapters += total;
                } else {
                    hasUnknownTotal = true;
                }
                readChapters += read;
            });

            const remaining = hasUnknownTotal
                ? Math.max(1, totalChapters - readChapters) // At least 1 if unknown
                : Math.max(0, totalChapters - readChapters);

            return {
                ...group,
                remainingChapters: remaining,
                totalChapters: hasUnknownTotal ? null : totalChapters,
                readChapters,
                hasNonDropped: true,
                hasUnknownTotal
            };
        });

        return groupsWithRemaining
            .filter(group => group.hasNonDropped && group.remainingChapters > 0)
            .sort((a, b) => b.remainingChapters - a.remainingChapters);
    }, [mangaGroups, latestChapterMap]);

    // Paginated groups
    const displayedGroups = useMemo(() => {
        let groupsToShow = [];
        Array.from(loadedPages).sort((a, b) => a - b).forEach(page => {
            const startIndex = (page - 1) * GROUPS_PER_PAGE;
            const endIndex = startIndex + GROUPS_PER_PAGE;
            groupsToShow.push(...marathonGroups.slice(startIndex, endIndex));
        });
        return groupsToShow;
    }, [marathonGroups, loadedPages]);

    const totalPages = Math.ceil(marathonGroups.length / GROUPS_PER_PAGE);

    const loadNextPage = useCallback(() => {
        const nextPage = currentPage + 1;
        if (nextPage <= totalPages && !loadedPages.has(nextPage)) {
            setLoadedPages(prev => new Set([...prev, nextPage]));
            setCurrentPage(nextPage);
        }
    }, [currentPage, totalPages, loadedPages]);

    // Infinite scroll
    const handleScroll = useCallback(() => {
        const scrollPosition = window.innerHeight + window.scrollY;
        const pageHeight = document.documentElement.scrollHeight;
        if (scrollPosition >= pageHeight - 1000 && currentPage < totalPages && !loadedPages.has(currentPage + 1)) {
            loadNextPage();
        }
    }, [currentPage, totalPages, loadedPages, loadNextPage]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    const handleGroupClick = (groupId) => {
        const group = mangaGroups.find(g => g.id === groupId);
        if (group) setSelectedGroup(group);
    };

    // Handle manga status change
    const handleStatusChange = async (mediaId, currentStatus, newStatus, mangaTitle) => {
        if (newStatus === currentStatus) return;
        const tkn = token || getSimpleCache(LOCAL_STORAGE_KEYS.TOKEN);
        if (!tkn) { showError('Authentication required.', 'Auth Required'); return; }

        setOptimisticUpdates(prev => new Map(prev).set(mediaId, newStatus));
        setChangingStatus(prev => new Set([...prev, mediaId]));

        try {
            await updateAnimeStatus(mediaId, newStatus, tkn);
            if (refreshData) refreshData(true);
            setChangingStatus(prev => { const next = new Set(prev); next.delete(mediaId); return next; });
        } catch (error) {
            setOptimisticUpdates(prev => { const next = new Map(prev); next.delete(mediaId); return next; });
            setChangingStatus(prev => { const next = new Set(prev); next.delete(mediaId); return next; });
            showError(`Failed to update status: ${error.message}`, 'Status Update Failed');
        }
    };

    // Handle score change
    const handleScoreChange = async (mediaId, newScoreRaw, mangaTitle) => {
        const tkn = token || getSimpleCache(LOCAL_STORAGE_KEYS.TOKEN);
        if (!tkn) { showError('Authentication required.', 'Auth Required'); return; }
        setOptimisticScores(prev => new Map(prev).set(mediaId, newScoreRaw));
        setUpdatingScores(prev => new Set([...prev, mediaId]));
        try {
            await updateMediaScore(mediaId, newScoreRaw, tkn);
            if (refreshData) refreshData(true);
        } catch (error) {
            showError(`Failed to update score for ${mangaTitle}`, 'Score Update Failed');
            setOptimisticScores(prev => { const n = new Map(prev); n.delete(mediaId); return n; });
        } finally {
            setUpdatingScores(prev => { const n = new Set(prev); n.delete(mediaId); return n; });
        }
    };

    // Handle chapter progress change
    const handleProgressChange = async (mediaId, newProgress, totalChapters, mangaTitle, mediaStatus, currentUserStatus) => {
        let clamped = Math.max(0, newProgress);
        if (totalChapters > 0) clamped = Math.min(clamped, totalChapters);
        const tkn = token || getSimpleCache(LOCAL_STORAGE_KEYS.TOKEN);
        if (!tkn) { showError('Authentication required.', 'Auth Required'); return; }

        setOptimisticProgress(prev => new Map(prev).set(mediaId, clamped));
        setUpdatingProgress(prev => new Set([...prev, mediaId]));

        try {
            await updateMangaProgress(mediaId, clamped, tkn);
            if (refreshData) refreshData(true);

            // Auto-start: if progress > 0 and status is PLANNING, switch to CURRENT (Reading)
            if (clamped > 0 && currentUserStatus === 'PLANNING') {
                console.log(`▶️ Auto-starting ${mangaTitle} — progress set to ${clamped}, switching to Reading`);
                handleStatusChange(mediaId, currentUserStatus, 'CURRENT', mangaTitle);
            }

            // Auto-complete: if all chapters read and manga is finished, mark as COMPLETED
            if (clamped > 0 && totalChapters > 0 && clamped === totalChapters && mediaStatus === 'FINISHED' && currentUserStatus !== 'COMPLETED') {
                console.log(`🎉 Auto-completing ${mangaTitle} — all ${totalChapters} chapters read`);
                handleStatusChange(mediaId, currentUserStatus, 'COMPLETED', mangaTitle);
            }
        } catch (error) {
            showError(`Failed to update progress for ${mangaTitle}`, 'Update Failed');
            setOptimisticProgress(prev => { const next = new Map(prev); next.delete(mediaId); return next; });
        } finally {
            setUpdatingProgress(prev => { const next = new Set(prev); next.delete(mediaId); return next; });
        }
    };

    // Handle volume progress change
    const handleVolumeProgress = async (mediaId, newVolumes, totalVolumes, mangaTitle, mediaStatus, currentUserStatus) => {
        let clamped = Math.max(0, newVolumes);
        if (totalVolumes > 0) clamped = Math.min(clamped, totalVolumes);

        if (!token) {
            showError('Authentication required.', 'Auth Required');
            return;
        }

        setOptimisticVolumes(prev => new Map(prev).set(mediaId, clamped));
        setUpdatingVolumes(prev => new Set([...prev, mediaId]));

        try {
            await updateMangaVolumeProgress(mediaId, clamped, token);
            if (refreshData) refreshData(true);

            // Auto-start: if progress > 0 and status is PLANNING, switch to CURRENT (Reading)
            if (clamped > 0 && currentUserStatus === 'PLANNING') {
                console.log(`▶️ Auto-starting ${mangaTitle} — volume progress set to ${clamped}, switching to Reading`);
                handleStatusChange(mediaId, currentUserStatus, 'CURRENT', mangaTitle);
            }

            // Auto-complete: if all volumes read and manga is finished, mark as COMPLETED
            if (clamped > 0 && totalVolumes > 0 && clamped === totalVolumes && mediaStatus === 'FINISHED' && currentUserStatus !== 'COMPLETED') {
                console.log(`🎉 Auto-completing ${mangaTitle} — all ${totalVolumes} volumes read`);
                handleStatusChange(mediaId, currentUserStatus, 'COMPLETED', mangaTitle);
            }
        } catch (error) {
            showError(`Failed to update volume for ${mangaTitle}`, 'Update Failed');
            setOptimisticVolumes(prev => { const n = new Map(prev); n.delete(mediaId); return n; });
        } finally {
            setUpdatingVolumes(prev => { const n = new Set(prev); n.delete(mediaId); return n; });
        }
    };

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (selectedGroup) document.body.style.overflow = 'hidden';
        else document.body.style.overflow = 'unset';
        return () => { document.body.style.overflow = 'unset'; };
    }, [selectedGroup]);

    if (isLoading && (!mangaGroups || mangaGroups.length === 0)) {
        return <CardGridSkeleton title="⏳ Marathon Mode" subtitle="Manga groups with the longest reading journey ahead" />;
    }

    if (!mangaGroups || mangaGroups.length === 0) {
        return (
            <div className="marathon-container">
                <div className="empty-state">
                    <div className="empty-icon">⏳</div>
                    <h2>No Groups Yet</h2>
                    <p>Your manga groups will appear here once loaded.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="marathon-container">
            <div className="marathon-header">
                <h1>⏳ Marathon Mode</h1>
                <p>Manga groups with the longest reading journey ahead</p>
                <div className="marathon-count">
                    Showing {displayedGroups.length} of {marathonGroups.length} groups
                    {loadedPages.size < totalPages && ` • Page ${loadedPages.size} of ${totalPages}`}
                </div>
            </div>

            <div className="marathon-groups-list">
                {displayedGroups.map((group) => {
                    const entries = group.anime || group.manga || [];
                    const oldestEntry = entries[0];
                    const coverImage = oldestEntry?.media?.coverImage?.extraLarge || oldestEntry?.media?.coverImage?.large || oldestEntry?.media?.coverImage?.medium;
                    const groupName = getDisplayTitle(oldestEntry?.media, titlePreference);
                    const hasReleasing = entries.some(a => a.media?.status === 'RELEASING');
                    const actualIndex = marathonGroups.findIndex(g => g.id === group.id);

                    return (
                        <div
                            key={group.id}
                            className="marathon-group-card"
                            onClick={() => handleGroupClick(group.id)}
                            role="button"
                            tabIndex={0}
                            onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') handleGroupClick(group.id); }}
                        >
                            <div className="marathon-rank">#{actualIndex + 1}</div>

                            {coverImage && (
                                <img src={coverImage} alt={groupName} className="marathon-cover" />
                            )}

                            <div className="marathon-group-info">
                                <h3 className="marathon-group-name">
                                    <a
                                        href={`https://anilist.co/manga/${oldestEntry?.media?.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {groupName}
                                    </a>
                                </h3>
                                {hasReleasing && <span className="group-releasing-tag">📡 Publishing</span>}

                                <div className="marathon-stats">
                                    <span className="stat-anime-count">
                                        📚 {entries.length} manga
                                    </span>
                                    <span>•</span>
                                    <span className="stat-episodes">
                                        📖 {formatChapterProgress(group.readChapters, group.totalChapters)} ch
                                    </span>
                                    {group.stats?.averageScore > 0 && (
                                        <>
                                            <span>•</span>
                                            <span className="stat-score">
                                                ⭐ {(group.stats.averageScore / 10).toFixed(1)}
                                            </span>
                                        </>
                                    )}
                                </div>

                                <div className="marathon-time-stats">
                                    <div className="time-stat remaining">
                                        <span className="time-label">⏳ Remaining</span>
                                        <span className="time-value">
                                            {group.remainingChapters} ch
                                            {(group.stats?.remainingVolumes || 0) > 0 && ` / ${group.stats.remainingVolumes} vol`}
                                        </span>
                                    </div>
                                    {group.readChapters > 0 && (
                                        <div className="time-stat watched">
                                            <span className="time-label">✅ Read</span>
                                            <span className="time-value">
                                                {group.readChapters} ch
                                                {(group.stats?.readVolumes || 0) > 0 && ` / ${group.stats.readVolumes} vol`}
                                            </span>
                                        </div>
                                    )}
                                    <div className="time-stat total">
                                        <span className="time-label">📖 Total</span>
                                        <span className="time-value">
                                            {group.totalChapters || '?'} ch
                                            {(group.stats?.totalVolumes || 0) > 0 && ` / ${group.stats.totalVolumes} vol`}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
                {/* Load More Button */}
                {loadedPages.size < totalPages && (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <button onClick={loadNextPage} className="force-rebuild-btn" style={{ margin: '0 auto' }}>
                            📥 Load More ({displayedGroups.length} / {marathonGroups.length})
                        </button>
                    </div>
                )}
            </div>

            {/* Group Card Modal Overlay */}
            {selectedGroup && (
                <div className="group-modal-overlay" onClick={() => setSelectedGroup(null)}>
                    <div className="group-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="close-btn" onClick={() => setSelectedGroup(null)}>×</button>

                        <div className="group-card-modal">
                            <div className="modal-group-header">
                                {(selectedGroup.anime || selectedGroup.manga)?.[0]?.media?.coverImage?.large && (
                                    <img
                                        src={(selectedGroup.anime || selectedGroup.manga)[0].media.coverImage.large}
                                        alt={getDisplayTitle((selectedGroup.anime || selectedGroup.manga)[0]?.media, titlePreference)}
                                        className="modal-group-cover"
                                    />
                                )}
                                <div className="modal-group-info">
                                    <h2 className="modal-group-name">
                                        📚 {getDisplayTitle((selectedGroup.anime || selectedGroup.manga)?.[0]?.media, titlePreference)}
                                    </h2>
                                    <div className="modal-group-stats">
                                        <span className="stat-anime-count">{(selectedGroup.anime || selectedGroup.manga)?.length || 0} manga</span>
                                        <span>•</span>
                                        <span className="stat-episodes">
                                            📖 {formatChapterProgress(selectedGroup.stats?.readChapters || 0, selectedGroup.stats?.totalChapters)} ch
                                        </span>
                                        {(selectedGroup.stats?.remainingChapters || 0) > 0 && (
                                            <>
                                                <span>•</span>
                                                <span className="stat-remaining-time">⏳ {selectedGroup.stats.remainingChapters} ch{selectedGroup.stats?.remainingVolumes > 0 ? ` / ${selectedGroup.stats.remainingVolumes} vol` : ''} remaining</span>
                                            </>
                                        )}
                                        {selectedGroup.stats?.averageScore > 0 && (
                                            <>
                                                <span>•</span>
                                                <span className="stat-score">⭐ {(selectedGroup.stats.averageScore / 10).toFixed(1)}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="modal-anime-list">
                                {(selectedGroup.anime || selectedGroup.manga || []).map((entry, index) => {
                                    const title = getDisplayTitle(entry.media, titlePreference);
                                    const releaseDate = formatReleaseDate(entry.media.startDate);
                                    const totalChapters = getTotalChapters(entry, latestChapterMap);
                                    const totalVolumes = getTotalVolumes(entry);
                                    const readChapters = entry.progress || 0;
                                    const readVolumes = entry.progressVolumes || 0;
                                    const displayStatus = optimisticUpdates.get(entry.media.id) || entry.status;
                                    const readChaptersDisplay = optimisticProgress.has(entry.media.id)
                                        ? optimisticProgress.get(entry.media.id) : readChapters;
                                    const mangaProgress = calculateMangaProgress(entry, latestChapterMap);

                                    return (
                                        <div key={entry.id} className="modal-anime-item">
                                            <img
                                                src={entry.media.coverImage?.extraLarge || entry.media.coverImage?.large || entry.media.coverImage?.medium}
                                                alt={title}
                                                className="anime-cover"
                                            />
                                            <div className="anime-details">
                                                <div className="anime-title-row">
                                                    <h4 className="anime-title">
                                                        <a href={`https://anilist.co/manga/${entry.media.id}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                                            {title}
                                                        </a>
                                                    </h4>
                                                    <div className="anime-action-buttons">
                                                        <ScoreInput
                                                            mediaId={entry.media.id}
                                                            currentScore={optimisticScores.has(entry.media.id) ? optimisticScores.get(entry.media.id) : (entry.score || 0)}
                                                            isUpdating={updatingScores.has(entry.media.id)}
                                                            onChange={(newScore) => handleScoreChange(entry.media.id, newScore, title)}
                                                        />
                                                        <EpisodeCounter
                                                            mediaId={entry.media.id}
                                                            currentProgress={readChaptersDisplay}
                                                            totalEpisodes={totalChapters}
                                                            isUpdating={updatingProgress.has(entry.media.id)}
                                                            onChange={(newVal) => handleProgressChange(entry.media.id, newVal, totalChapters, title, entry.media.status, displayStatus)}
                                                            label="chapter"
                                                        />
                                                        {totalVolumes > 0 && (
                                                            <EpisodeCounter
                                                                mediaId={`vol-${entry.media.id}`}
                                                                currentProgress={optimisticVolumes.has(entry.media.id) ? optimisticVolumes.get(entry.media.id) : readVolumes}
                                                                totalEpisodes={totalVolumes}
                                                                isUpdating={updatingVolumes.has(entry.media.id)}
                                                                onChange={(newVal) => handleVolumeProgress(entry.media.id, newVal, totalVolumes, title, entry.media.status, displayStatus)}
                                                                label="volume"
                                                            />
                                                        )}
                                                        <StatusButton
                                                            currentStatus={displayStatus}
                                                            onChange={(newStatus) => handleStatusChange(entry.media.id, displayStatus, newStatus, title)}
                                                            disabled={changingStatus.has(entry.media.id)}
                                                            isLoading={changingStatus.has(entry.media.id)}
                                                            type="manga"
                                                        />
                                                        <button
                                                            className="action-btn watch-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedMangaForModal(title);
                                                                setShowReadModal(true);
                                                            }}
                                                            title="Read"
                                                        >
                                                            📖
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="anime-meta">
                                                    <span>{releaseDate}</span>
                                                    <span>{entry.media.format}</span>
                                                    <span>📖 {readChapters}/{totalChapters || '?'} ch</span>
                                                    {totalVolumes > 0 && <span>📕 {readVolumes}/{totalVolumes} vol</span>}
                                                    {mangaProgress.remainingChapters > 0 && (
                                                        <span>⏳ {mangaProgress.remainingChapters} ch{mangaProgress.remainingVolumes > 0 ? ` / ${mangaProgress.remainingVolumes} vol` : ''}</span>
                                                    )}
                                                    {entry.score > 0 && <span>⭐ {(entry.score / 10).toFixed(1)}</span>}
                                                    {entry.media.averageScore > 0 && <span>📊 {(entry.media.averageScore / 10).toFixed(1)}</span>}
                                                    {(() => { const badge = getMangaMediaStatusBadge(entry.media?.status); return <span className={`media-status-badge ${badge.className}`}>{badge.emoji} {badge.label}</span>; })()}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Read Links Modal */}
            <SiteLinksModal
                isOpen={showReadModal}
                onClose={() => {
                    setShowReadModal(false);
                    setSelectedMangaForModal('');
                }}
                links={readLinks}
                animeName={selectedMangaForModal || ''}
                type="read"
            />
        </div>
    );
};

export default MangaMarathonMode;
